import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { FileText, Eye, Calendar, Clock, CheckSquare2 } from 'lucide-react';
import ResponsiveTooltip from '../ui/ResponsiveTooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format } from 'date-fns';
import { truncateText } from '../../lib/utils';
import Pagination from '../Pagination';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const MyUnderReviewTasksTable = ({
    tasks = [],
    page,
    pages,
    total,
    pageSize,
    hasNext,
    hasPrev,
    onPageChange,
    onPageSizeChange
}) => {
    const [viewCommentOpen, setViewCommentOpen] = useState(false);
    const [viewCommentText, setViewCommentText] = useState('');
    const [sortColumn, setSortColumn] = useState('due_date');
    const [sortDirection, setSortDirection] = useState('asc');

    const formatDate = (dateString) => {
        if (!dateString) return 'No due date';
        try {
            return format(new Date(dateString), 'MMM dd, yyyy');
        } catch {
            return 'Invalid date';
        }
    };

    const getPendingDays = (task) => {
        if (!task.due_date) return null;
        const dueDate = new Date(task.due_date);
        dueDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diffTime = dueDate - today;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (column) => {
        if (sortColumn === column) {
            return (
                <span className="ml-1 text-gray-400">
                    {sortDirection === 'asc' ? '▲' : '▼'}
                </span>
            );
        }
        return null;
    };

    const sortedTasks = [...tasks].sort((a, b) => {
        let compareValue = 0;
        if (sortColumn === 'due_date') {
            const dateA = a.due_date ? new Date(a.due_date).getTime() : 0;
            const dateB = b.due_date ? new Date(b.due_date).getTime() : 0;
            compareValue = dateA - dateB;
        } else if (sortColumn === 'task') {
            const descA = (a.description || '').toLowerCase();
            const descB = (b.description || '').toLowerCase();
            compareValue = descA.localeCompare(descB);
        } else if (sortColumn === 'pending_days') {
            const daysA = getPendingDays(a);
            const daysB = getPendingDays(b);
            if (daysA === null) return 1;
            if (daysB === null) return -1;
            compareValue = daysA - daysB;
        }
        return sortDirection === 'asc' ? compareValue : -compareValue;
    });

    if (tasks.length === 0) {
        return (
            <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                    <Clock className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Tasks Under Review</h3>
                    <p className="text-muted-foreground text-center">
                        You don't have any tasks currently awaiting approval.
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card>
                <CardContent>
                    <div className="hidden md:block border rounded-lg mt-2 overflow-x-auto">
                        <div className="max-h-[calc(100vh-290px)] overflow-auto">
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm">
                                    <tr className="border-b">
                                        <th className="h-10 px-2 text-center font-medium text-muted-foreground">Sr. No.</th>
                                        <th
                                            className="h-10 px-2 text-left font-medium text-muted-foreground cursor-pointer hover:bg-gray-200 transition-colors"
                                            onClick={() => handleSort('task')}
                                        >
                                            <div className="flex items-center">Task {getSortIcon('task')}</div>
                                        </th>
                                        <th className="h-10 px-2 text-center font-medium text-muted-foreground">Approver</th>
                                        <th
                                            className="h-10 px-2 text-center font-medium text-muted-foreground cursor-pointer hover:bg-gray-200 transition-colors"
                                            onClick={() => handleSort('due_date')}
                                        >
                                            <div className="flex items-center justify-center gap-2">
                                                Due Date <Calendar className="w-4 h-4 text-gray-400" /> {getSortIcon('due_date')}
                                            </div>
                                        </th>
                                        <th
                                            className="h-10 px-2 text-center font-medium text-muted-foreground cursor-pointer hover:bg-gray-200 transition-colors"
                                            onClick={() => handleSort('pending_days')}
                                        >
                                            <div className="flex items-center justify-center">Pending Days {getSortIcon('pending_days')}</div>
                                        </th>
                                        <th className="h-10 px-2 text-center font-medium text-muted-foreground">Status</th>
                                        <th className="h-10 px-2 text-center font-medium text-muted-foreground">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedTasks.map((task, index) => (
                                        <tr key={task.id} className="border-b hover:bg-gray-50 transition-colors">
                                            <td className="p-2 text-center">{(page - 1) * pageSize + index + 1}</td>
                                            <td className="p-2 text-left w-72">
                                                <div className="space-y-1">
                                                    {task.description?.length > 20 ? (
                                                        <ResponsiveTooltip content={<p className="text-sm whitespace-pre-wrap">{task.description}</p>}>
                                                            <div className="font-medium cursor-pointer">{truncateText(task.description, 20)}</div>
                                                        </ResponsiveTooltip>
                                                    ) : (
                                                        <div className="font-medium">{task.description}</div>
                                                    )}
                                                    {task.meeting?.title && (
                                                        <span className="text-xs text-gray-500">Meeting: {truncateText(task.meeting.title, 30)}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-2 text-center text-sm">{task.approver?.full_name || 'N/A'}</td>
                                            <td className="p-2 text-center">{formatDate(task.due_date)}</td>
                                            <td className="p-2 text-center">
                                                {(() => {
                                                    const days = getPendingDays(task);
                                                    if (days === null) return <Badge variant="secondary">N/A</Badge>;
                                                    const color = days < 0 ? 'bg-red-500' : 'bg-[#FFC107]';
                                                    return <span className={`text-white ${color} font-medium px-2 py-1 rounded text-xs`}>{days}</span>;
                                                })()}
                                            </td>
                                            <td className="p-2 text-center">
                                                <Badge variant="outline" className="text-orange-600 border-orange-200 bg-orange-50">Under Review</Badge>
                                            </td>
                                            <td className="p-2 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    {task.document_url && (
                                                        <Button size="sm" variant="outline" onClick={() => window.open(task.document_url, '_blank')}>
                                                            <FileText className="h-4 w-4 mr-1" /> View Doc
                                                        </Button>
                                                    )}
                                                    <Button size="sm" variant="outline" onClick={() => {
                                                        setViewCommentText(task.assignee_comment || 'No remark provided with submission');
                                                        setViewCommentOpen(true);
                                                    }}>
                                                        <Eye className="h-4 w-4 mr-1" /> Remark
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="md:hidden mt-2">
                        <Accordion type="single" collapsible className="w-full space-y-2">
                            {sortedTasks.map((task, index) => (
                                <AccordionItem key={task.id} value={`review-${task.id}`} className="border rounded-lg px-4 bg-white">
                                    <AccordionTrigger className="hover:no-underline py-4">
                                        <div className="flex items-center gap-3 w-full pr-4">
                                            <span className="text-sm font-medium text-gray-500">{(page - 1) * pageSize + index + 1}.</span>
                                            <div className="text-left flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">{task.description}</p>
                                                <Badge variant="outline" className="mt-1 text-[10px] h-4 text-orange-600 bg-orange-50">Under Review</Badge>
                                            </div>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="pb-4 pt-0">
                                        <div className="space-y-3 pt-2 border-t">
                                            {task.meeting?.title && (
                                                <div>
                                                    <p className="text-xs font-medium text-gray-500 mb-1">Meeting</p>
                                                    <p className="text-sm text-gray-900">{task.meeting.title}</p>
                                                </div>
                                            )}
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <p className="text-xs font-medium text-gray-500 mb-1">Approver</p>
                                                    <p className="text-sm text-gray-900">{task.approver?.full_name || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-medium text-gray-500 mb-1">Due Date</p>
                                                    <p className="text-sm text-gray-900">{formatDate(task.due_date)}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between gap-2">
                                                {task.document_url && (
                                                    <Button size="sm" variant="outline" className="flex-1" onClick={() => window.open(task.document_url, '_blank')}>
                                                        <FileText className="h-4 w-4 mr-1" /> View Doc
                                                    </Button>
                                                )}
                                                <Button size="sm" variant="outline" className="flex-1" onClick={() => {
                                                    setViewCommentText(task.assignee_comment || 'No remark provided');
                                                    setViewCommentOpen(true);
                                                }}>
                                                    <Eye className="h-4 w-4 mr-1" /> Remark
                                                </Button>
                                            </div>
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>

                    {total > 0 && (
                        <Pagination
                            page={page}
                            pages={pages}
                            total={total}
                            pageSize={pageSize}
                            hasNext={hasNext}
                            hasPrev={hasPrev}
                            onPageChange={onPageChange}
                            onPageSizeChange={onPageSizeChange}
                        />
                    )}
                </CardContent>
            </Card>

            <Dialog open={viewCommentOpen} onOpenChange={() => setViewCommentOpen(false)}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>Submission Remark:</DialogTitle>
                    </DialogHeader>
                    <div className="whitespace-pre-wrap text-sm text-foreground">
                        {viewCommentText}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MyUnderReviewTasksTable;
